import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { CustomDateAdapter } from '../../../shared/services/custom-date-adapter';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subject, takeUntil } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { AutocompletePesquisavelComponent } from '../../../shared/components/autocomplete-pesquisavel/autocomplete-pesquisavel.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { PercentualProgressBarComponent } from '../../../shared/components/percentual-progress-bar/percentual-progress-bar.component';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import {
  RateioConfigPickerModalComponent,
  RateioConfigPickerResultItem,
} from '../../../shared/components/rateio-config-picker-modal/rateio-config-picker-modal.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { TipoContaEnum } from '../../../shared/enums/financas/tipo-conta.enum';
import { ContasPagarUtil } from '@shared/helpers/contas-pagar.util';
import { MovimentacaoService } from '../../../shared/services/apis/financas/movimentacao.service';
import { CategoriaFinanceira } from '../../../shared/services/models/financas/categoria-financeira.model';
import { RateioCentroCusto } from '../../../shared/services/models/financas/rateio-centro-custo.model';
import { CentroCusto } from '../../../shared/services/models/financas/centro-custo.model';
import { ContaFinanceiraListItem } from '../../../shared/services/structs/financas/conta-financeira-list-item.struct';
import {
  CreateMovimentacaoRequest,
  MovimentacaoArquivoItem,
} from '../../../shared/services/models/financas/create-movimentacao.request';
import {
  MovimentacaoArquivoDetalhe,
  MovimentacaoDetalhe,
} from '../../../shared/services/responses/financas/get-movimentacao-by-id.response';
import { TagMovimentacao } from '../../../shared/services/models/financas/tag-movimentacao.model';
import { MaskService } from '../../../shared/services/mask.service';
import { PessoaLookupService } from '../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ListPessoaIdsRequest } from '../../../shared/services/requests/pessoa/list-pessoa-ids-request';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { UtilService } from '@shared/services/util.service';

interface SelectOption {
  id: number;
  descricao: string;
  disabled?: boolean;
}

interface PessoaAutocompleteOption {
  id: number;
  descricao: string;
}

interface AnexoLancamento {
  idMovimentacaoArquivo?: number;
  nomeArquivo: string;
  extensaoArquivo: string;
  tamanhoBytes: number;
  base64: string;
  urlArquivo?: string;
}

@Component({
  selector: 'app-lancamento-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatDialogModule,
    NgxCurrencyDirective,
    NgxEditorModule,
    HeaderComponent,
    LoadingBoxComponent,
    PercentualProgressBarComponent,
    FooterActionComponent,
    AutocompletePesquisavelComponent,
    SelectPesquisavelComponent,
    AlertCardComponent,
  ],
  templateUrl: './lancamento-cadastro.component.html',
  styleUrl: './lancamento-cadastro.component.css',
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
})
export class LancamentoCadastroComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Financas;
  public funcionalidadeEnum = FuncionalidadeEnum.Financas_Extrato;

  public form!: FormGroup;
  public isLoading = false;
  public isSaving = false;
  public isEditMode = false;
  /** Pedido de venda que gerou a movimentação: a tela fica em leitura (altere ou exclua pelo pedido). */
  public idVenda: number | null = null;
  /** NF-e de saída que gerou a movimentação: a tela fica em leitura (altere ou exclua pela nota). */
  public idNFeSaida: number | null = null;

  public editor!: Editor;
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['link'],
  ];

  public currencyMask = this.maskService.getCurrencyMask(2);
  public decimalMask = this.maskService.getDecimalMask2();

  public contaOptions: SelectOption[] = [];
  public contaFinanceiraList: ContaFinanceiraListItem[] = [];
  public categoriaOptions: SelectOption[] = [];
  public centroCustoOptions: SelectOption[] = [];
  public centrosCustoLookup: CentroCusto[] = [];
  public rateiosCentroCustoLookup: RateioCentroCusto[] = [];
  public tagOptions: TagMovimentacao[] = [];
  public readonly urlPessoaLookup = `${environment.urlApiPessoa}Lookup/listPessoaSelect`;
  public pessoaSelecionada: PessoaAutocompleteOption | null = null;

  public anexos: AnexoLancamento[] = [];

  public readonly maxFileSize = 5 * 1024 * 1024;
  private readonly acceptedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.xml'];
  private readonly destroy$ = new Subject<void>();
  private readonly tipoReceita = 1;
  private readonly tipoDespesa = 2;
  private categoriasLookup: CategoriaFinanceira[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private movimentacaoService: MovimentacaoService,
    private maskService: MaskService,
    private pessoaLookupService: PessoaLookupService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.isEditMode = !!this.route.snapshot.paramMap.get('id');
    this.editor = new Editor();
    this.createForm();
    this.configureReactiveBehavior();
    this.loadLookups();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.editor.destroy();
  }

  public get tituloPagina(): string {
    return this.isEditMode ? 'Editar Lançamento' : 'Novo Lançamento';
  }

  public get rateiosFormArray(): FormArray {
    return this.form.get('rateios') as FormArray;
  }

  public get idContaOrigemControl(): FormControl {
    return this.form.get('idContaOrigem') as FormControl;
  }

  public get tipoControl(): FormControl {
    return this.form.get('tipo') as FormControl;
  }

  public get categoriaControl(): FormControl {
    return this.form.get('idCategoriaFinanceira') as FormControl;
  }

  public get centroCustoControl(): FormControl {
    return this.form.get('idCentroCusto') as FormControl;
  }

  public get rateioToggleControl(): FormControl {
    return this.form.get('isRateioCentroCusto') as FormControl;
  }

  public get idPessoaControl(): FormControl {
    return this.form.get('idPessoa') as FormControl;
  }

  public get valorControl(): FormControl {
    return this.form.get('valor') as FormControl;
  }

  public get tagsControl(): FormControl {
    return this.form.get('tags') as FormControl;
  }

  public get totalPercentualRateio(): number {
    return this.rateiosFormArray.controls.reduce((total, item) => {
      const percentual = Number(item.get('percentual')?.value || 0);
      return total + percentual;
    }, 0);
  }

  public get rateioTotalValido(): boolean {
    if (!this.rateioToggleControl.value) {
      return true;
    }

    return Math.abs(this.totalPercentualRateio - 100) <= 0.01;
  }

  public get contaSelecionada(): ContaFinanceiraListItem | null {
    const id = this.idContaOrigemControl?.value;
    if (!id) return null;
    return this.contaFinanceiraList.find(c => (c.idContaFinanceira ?? c.idContaBancaria) === id) ?? null;
  }

  public get isContaCartao(): boolean {
    return this.contaSelecionada?.idTipoConta === TipoContaEnum.CartaoCredito;
  }

  public get diaFechamentoCartao(): number | null {
    return this.contaSelecionada?.diaFechamentoCartao ?? null;
  }

  public get diaVencimentoCartao(): number | null {
    return this.contaSelecionada?.diaVencimentoCartao ?? null;
  }

  public cartaoDateFilter = (d: Date | null): boolean => {
    if (!d) return false;
    if (!this.isContaCartao) return true;
    const fechamento = this.diaFechamentoCartao;
    const vencimento = this.diaVencimentoCartao;
    if (fechamento == null || vencimento == null) return true;
    const mesMinimo = ContasPagarUtil.calcularMesFaturaDestino(new Date(), fechamento, vencimento);
    return new Date(d.getFullYear(), d.getMonth(), 1) >= mesMinimo;
  };

  public get categoriasFiltradas(): SelectOption[] {
    const tipo = this.tipoControl.value;
    if (!tipo) {
      return this.categoriasLookup
        .filter(item => item.isAtivo)
        .map(item => ({
          id: item.idCategoriaFinanceira,
          descricao: item.descricao,
        }));
    }

    const tipoCategoria = Number(tipo) === this.tipoReceita ? this.tipoReceita : this.tipoDespesa;
    return this.categoriasLookup
      .filter(item => item.isAtivo && item.idTipoCategoriaFinanceira === tipoCategoria)
      .map(item => ({
        id: item.idCategoriaFinanceira,
        descricao: item.descricao,
      }));
  }

  public voltar(): void {
    this.router.navigate(['/financas/extrato-movimentacoes']);
  }

  public onMonthSelected(date: Date, datepicker: MatDatepicker<Date>, control: AbstractControl | null): void {
    if (!this.isContaCartao || !control) return;
    const dia = this.diaVencimentoCartao;
    const adjusted = dia != null ? ContasPagarUtil.ajustarParaDiaVencimento(date, dia) : date;
    control.setValue(adjusted);
    datepicker.close();
  }

  public onDateInput(event: Event): void {
    MaskService.onDateInput(event, this.form.get('data'));
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios do lançamento.', AlertType.warning);
      return;
    }

    if (this.rateioToggleControl.value) {
      if (!this.rateiosFormArray.length) {
        this.alertService.show(
          'Atenção',
          'Adicione ao menos uma linha de rateio por centro de custo.',
          AlertType.warning
        );
        return;
      }

      const possuiLinhaInvalida = this.rateiosFormArray.controls.some(
        item =>
          !item.get('idCentroCusto')?.value || (!item.get('valor')?.value && !item.get('percentual')?.value)
      );

      if (possuiLinhaInvalida) {
        this.alertService.show(
          'Atenção',
          'Preencha centro de custo e valor ou percentual em todas as linhas do rateio.',
          AlertType.warning
        );
        return;
      }

      if (!this.rateioTotalValido) {
        this.alertService.show(
          'Atenção',
          'O rateio precisa totalizar 100% do valor da movimentação.',
          AlertType.warning
        );
        return;
      }
    }

    const raw = this.form.getRawValue();
    const dataMovimentacao =
      raw.data instanceof Date ? raw.data.toISOString() : new Date(raw.data).toISOString();

    const request: CreateMovimentacaoRequest = {
      idContaFinanceira: raw.idContaOrigem,
      idNaturezaMovimentacao: raw.tipo,
      dataMovimentacao,
      valor: raw.valor,
      idCategoriaFinanceira: raw.idCategoriaFinanceira || null,
      idCentroCusto: raw.isRateioCentroCusto ? null : raw.idCentroCusto || null,
      isRateioCentroCusto: raw.isRateioCentroCusto,
      rateios: raw.isRateioCentroCusto
        ? (raw.rateios || []).map((r: any) => ({
          idCentroCusto: r.idCentroCusto,
          valor: r.valor || null,
          percentual: r.percentual || null,
        }))
        : [],
      idPessoa: raw.idPessoa || null,
      descricao: raw.historicoHtml || null,
      idsTagsMovimentacao: Array.isArray(raw.tags) ? raw.tags : [],
      arquivos: this.anexos.map(a => ({
        idMovimentacaoArquivo: a.idMovimentacaoArquivo ?? null,
        nomeArquivo: a.nomeArquivo,
        extensaoArquivo: a.extensaoArquivo,
        tamanhoBytes: a.tamanhoBytes,
        base64: a.base64 || null,
        urlArquivo: a.urlArquivo || null,
      })),
    };

    this.isSaving = true;
    const request$ = this.isEditMode
      ? this.movimentacaoService.update(Number(this.route.snapshot.paramMap.get('id')), request)
      : this.movimentacaoService.create(request);

    request$.subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          const isCartaoBlock = response.errorDescription?.includes('Cartão de Crédito');
          this.alertService.show(
            isCartaoBlock ? 'Atenção' : 'Erro',
            response.errorDescription || 'Não foi possível salvar o lançamento.',
            isCartaoBlock ? AlertType.warning : AlertType.error
          );
          return;
        }
        this.alertService.show('Sucesso', 'Lançamento salvo com sucesso.', AlertType.success);
        this.voltar();
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Erro ao salvar o lançamento.', AlertType.error);
      },
    });
  }

  public adicionarLinhaRateio(idCentroCusto: number | null = null, percentual: number | null = null): void {
    const group = this.formBuilder.group({
      idCentroCusto: [idCentroCusto, Validators.required],
      valor: [this.calcularValorPorPercentual(percentual ?? 0)],
      percentual: [percentual],
    });

    group
      .get('valor')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const index = this.rateiosFormArray.controls.indexOf(group);
        if (index >= 0) this.onRateioValorChange(index);
      });

    group
      .get('percentual')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const index = this.rateiosFormArray.controls.indexOf(group);
        if (index >= 0) this.onRateioPercentualChange(index);
      });

    this.rateiosFormArray.push(group);
  }

  public removerLinhaRateio(index: number): void {
    this.rateiosFormArray.removeAt(index);
  }

  public abrirSeletorRateio(): void {
    const possuiLinhasPreenchidas = this.rateiosFormArray.controls.some(
      ctrl => !!ctrl.get('idCentroCusto')?.value
    );

    if (!possuiLinhasPreenchidas) {
      this.abrirModalConfiguracaoRateio();
      return;
    }

    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Substituir rateio atual?',
        description:
          'Já existem linhas de rateio preenchidas. Ao selecionar uma configuração, todas as linhas atuais serão substituídas pela configuração escolhida. Deseja continuar?',
        buttonConfirm: 'Continuar',
        buttonClose: 'Cancelar',
      },
    });

    confirmRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this.abrirModalConfiguracaoRateio();
      }
    });
  }

  private abrirModalConfiguracaoRateio(): void {
    const dialogRef = this.dialog.open(RateioConfigPickerModalComponent, {
      width: '520px',
      data: {
        configs: this.rateiosCentroCustoLookup,
        centrosCusto: this.centrosCustoLookup,
      },
    });

    dialogRef.afterClosed().subscribe((itens?: RateioConfigPickerResultItem[]) => {
      if (!itens?.length) return;

      this.rateiosFormArray.clear();
      itens.forEach(item => this.adicionarLinhaRateio(item.idCentroCusto, item.percentual));
      this.recalcularValoresDoRateio();
    });
  }

  public onRateioValorChange(index: number): void {
    const control = this.rateiosFormArray.at(index);
    const valor = Number(control.get('valor')?.value || 0);
    const total = Number(this.valorControl.value || 0);
    const percentual = UtilService.arredondarAbnt(total > 0 ? Number(((valor / total) * 100).toFixed(2)) : 0, 2);

    control.get('percentual')?.setValue(percentual, { emitEvent: false });
  }

  public onRateioPercentualChange(index: number): void {
    const control = this.rateiosFormArray.at(index);
    const percentual = UtilService.arredondarAbnt(Number(control.get('percentual')?.value || 0), 2);
    control.get('valor')?.setValue(this.calcularValorPorPercentual(percentual), { emitEvent: false });
  }

  public obterCentrosCustoDisponiveis(indexAtual: number): SelectOption[] {
    const idsSelecionados = this.rateiosFormArray.controls
      .filter((_, index) => index !== indexAtual)
      .map(item => item.get('idCentroCusto')?.value)
      .filter(id => !!id);

    return this.centroCustoOptions.map(option => ({
      ...option,
      disabled: idsSelecionados.includes(option.id),
    }));
  }

  public onToggleRateio(checked: boolean): void {
    if (checked) {
      this.centroCustoControl.disable({ emitEvent: false });
      this.centroCustoControl.setValue(null, { emitEvent: false });
      if (!this.rateiosFormArray.length) {
        this.adicionarLinhaRateio();
      }
      return;
    }

    this.centroCustoControl.enable({ emitEvent: false });
    this.rateiosFormArray.clear();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.processFiles(input.files);
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.add('drag-over');
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.processFiles(files);
    }
  }

  public removerAnexo(index: number): void {
    this.anexos.splice(index, 1);
  }

  public baixarAnexo(arquivo: AnexoLancamento): void {
    const a = document.createElement('a');
    a.download = arquivo.nomeArquivo;

    if (arquivo.urlArquivo) {
      a.href = arquivo.urlArquivo;
      a.target = '_blank';
      a.click();
      return;
    }

    if (arquivo.base64) {
      const mime = this.getMimeType(arquivo.extensaoArquivo);
      const byteChars = atob(arquivo.base64);
      const byteNumbers = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const url = URL.createObjectURL(new Blob([byteNumbers], { type: mime }));
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  public formatBytes(bytes: number | null | undefined): string {
    if (!bytes) {
      return '0 Bytes';
    }

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  }

  public getControl(item: AbstractControl, field: string): FormControl {
    return item.get(field) as FormControl;
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      idContaOrigem: [null, Validators.required],
      tipo: [null, Validators.required],
      data: [new Date(), Validators.required],
      valor: [null, [Validators.required, Validators.min(0.01)]],
      idCategoriaFinanceira: [null],
      idCentroCusto: [null],
      isRateioCentroCusto: [false],
      idPessoa: [null],
      historicoHtml: ['', Validators.required],
      tags: [[] as number[]],
      rateios: this.formBuilder.array([]),
    });
  }

  private configureReactiveBehavior(): void {
    this.idContaOrigemControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isContaCartao && this.diaFechamentoCartao != null && this.diaVencimentoCartao != null) {
        const mesDestino = ContasPagarUtil.calcularMesFaturaDestino(
          new Date(), this.diaFechamentoCartao, this.diaVencimentoCartao
        );
        const dataCorreta = ContasPagarUtil.ajustarParaDiaVencimento(mesDestino, this.diaVencimentoCartao);
        this.form.get('data')?.setValue(dataCorreta, { emitEvent: false });
      }
    });

    this.tipoControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.categoriaOptions = this.categoriasFiltradas;

      const categoriaSelecionada = Number(this.categoriaControl.value || 0);
      const categoriaAindaDisponivel = this.categoriaOptions.some(item => item.id === categoriaSelecionada);
      if (!categoriaAindaDisponivel) {
        this.categoriaControl.setValue(null);
      }
    });

    this.rateioToggleControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(checked => this.onToggleRateio(!!checked));
    this.valorControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalcularValoresDoRateio());
  }

  private loadLookups(): void {
    this.isLoading = true;
    const idContaFinanceiraParam = Number(this.route.snapshot.queryParamMap.get('idContaFinanceira') || 0);

    this.movimentacaoService
      .getLookup(idContaFinanceiraParam > 0 ? idContaFinanceiraParam : undefined)
      .subscribe({
        next: response => {
          this.isLoading = false;

          if (response.isError) {
            this.alertService.show(
              'Erro',
              response.errorDescription || 'Não foi possível carregar os dados do formulário de lançamento.',
              AlertType.error
            );
            return;
          }

          this.contaFinanceiraList = response.listaContaFinanceira || [];
          this.contaOptions = this.contaFinanceiraList.map(item => this.mapContaToOption(item));
          this.categoriasLookup = response.listaCategoriasFinanceira || [];
          this.categoriaOptions = this.categoriasFiltradas;
          this.centrosCustoLookup = response.listaCentrosCusto || [];
          this.centroCustoOptions = this.centrosCustoLookup.map(item => ({
            id: item.idCentroCusto,
            descricao: item.nome,
          }));
          this.rateiosCentroCustoLookup = response.listaRateiosCentroCusto || [];
          this.tagOptions = response.listaTagsMovimentacao || [];

          if (response.idContaFinanceiraSelecionada && !this.isEditMode) {
            this.idContaOrigemControl.setValue(response.idContaFinanceiraSelecionada);
          }

          if (this.isEditMode) {
            const idMovimentacao = Number(this.route.snapshot.paramMap.get('id'));
            this.loadMovimentacao(idMovimentacao);
          }
        },
        error: () => {
          this.isLoading = false;
          this.alertService.show('Erro', 'Erro ao carregar lookups do lançamento.', AlertType.error);
        },
      });
  }

  private loadMovimentacao(id: number): void {
    this.isLoading = true;
    this.movimentacaoService.getById(id).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.isError || !response.movimentacao) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Lançamento não encontrado.',
            AlertType.error
          );
          this.voltar();
          return;
        }
        this.preencherFormulario(response.movimentacao);
        this.idVenda = response.movimentacao.idVenda ?? null;
        this.idNFeSaida = response.movimentacao.idNFeSaida ?? null;
        // Sem emitir: o disable dispararia o valueChanges do toggle de rateio, que reabilita o centro de custo.
        if (this.idVenda || this.idNFeSaida) this.form.disable({ emitEvent: false });
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao carregar lançamento.', AlertType.error);
        this.voltar();
      },
    });
  }

  private preencherFormulario(m: MovimentacaoDetalhe): void {
    // Set tipo without emitting so the categoria subscription doesn't clear the value before we patch it
    this.tipoControl.setValue(m.idNaturezaMovimentacao, { emitEvent: false });
    this.categoriaOptions = this.categoriasFiltradas;

    this.form.patchValue(
      {
        idContaOrigem: m.idContaFinanceira,
        data: new Date(m.dataMovimentacao),
        valor: m.valor,
        idCategoriaFinanceira: m.idCategoriaFinanceira,
        idCentroCusto: m.isRateioCentroCusto ? null : m.idCentroCusto,
        isRateioCentroCusto: m.isRateioCentroCusto,
        idPessoa: m.idPessoa,
        historicoHtml: m.descricao || '',
        tags: m.idsTagsMovimentacao,
      },
      { emitEvent: false }
    );

    this.carregarPessoaSelecionada(m.idPessoa);

    if (m.isRateioCentroCusto && m.rateios.length) {
      this.centroCustoControl.disable({ emitEvent: false });
      this.rateiosFormArray.clear();
      m.rateios.forEach(r => this.adicionarLinhaRateio(r.idCentroCusto, r.percentual));
    }

    this.anexos = m.arquivos.map((a: MovimentacaoArquivoDetalhe) => ({
      idMovimentacaoArquivo: a.idMovimentacaoArquivo,
      nomeArquivo: a.nomeArquivo || '',
      extensaoArquivo: a.extensaoArquivo || '',
      tamanhoBytes: a.tamanhoBytes || 0,
      base64: '',
      urlArquivo: a.urlArquivo || '',
    }));
  }

  private carregarPessoaSelecionada(idPessoa: number | null): void {
    this.pessoaSelecionada = null;

    if (!idPessoa) {
      return;
    }

    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = [idPessoa];

    this.pessoaLookupService.getListPessoaIds(request).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar cliente do lançamento.',
            AlertType.error
          );
          return;
        }

        const pessoa = response.listaPessoa?.find(item => item.idPessoa === idPessoa);
        if (!pessoa) {
          return;
        }

        this.pessoaSelecionada = {
          id: pessoa.idPessoa,
          descricao: pessoa.nome,
        };
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao carregar cliente do lançamento.', AlertType.error);
      },
    });
  }

  private mapContaToOption(item: ContaFinanceiraListItem): SelectOption {
    return {
      id: item.idContaFinanceira || item.idContaBancaria || 0,
      descricao: item.descricao,
    };
  }

  private recalcularValoresDoRateio(): void {
    if (!this.rateioToggleControl.value || !this.rateiosFormArray.length) {
      return;
    }

    this.rateiosFormArray.controls.forEach(item => {
      const percentual = Number(item.get('percentual')?.value || 0);
      item.get('valor')?.setValue(this.calcularValorPorPercentual(percentual), { emitEvent: false });
    });
  }

  private calcularValorPorPercentual(percentual: number): number {
    const total = Number(this.valorControl?.value || 0);
    if (total <= 0 || percentual <= 0) {
      return 0;
    }

    return UtilService.arredondarAbnt((total * percentual) / 100, 2);
  }

  private processFiles(files: FileList): void {
    Array.from(files).forEach(file => {
      const extensao = this.getExtensao(file.name);
      if (!this.acceptedExtensions.includes(extensao.toLowerCase())) {
        this.alertService.show(
          'Aviso',
          `O arquivo "${file.name}" não possui um formato permitido.`,
          AlertType.warning
        );
        return;
      }

      if (file.size > this.maxFileSize) {
        this.alertService.show(
          'Aviso',
          `O arquivo "${file.name}" excede o limite de 5MB.`,
          AlertType.warning
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        const base64 = String(event.target?.result || '').split(',')[1];
        this.anexos.push({
          nomeArquivo: file.name,
          extensaoArquivo: extensao,
          tamanhoBytes: file.size,
          base64,
        });
      };
      reader.readAsDataURL(file);
    });
  }

  private getExtensao(nomeArquivo: string): string {
    const index = nomeArquivo.lastIndexOf('.');
    return index >= 0 ? nomeArquivo.substring(index) : '';
  }

  private getMimeType(extensao: string): string {
    switch (extensao.toLowerCase()) {
      case '.pdf':
        return 'application/pdf';
      case '.png':
        return 'image/png';
      case '.jpg':
      case '.jpeg':
        return 'image/jpeg';
      case '.xml':
        return 'application/xml';
      default:
        return 'application/octet-stream';
    }
  }
}
